import { FC, useMemo } from "react";
import { startCase } from "lodash";

import { Icon } from "@/components";
import { Badge, IconButton, Table, Text, type TableColumn } from "@/ui";
import { currencyFormat } from "@/utils";
import { NOT_AVAILABLE } from "@/config";
import { LineItem, OrderLineItemProductName } from "@/types";
import useNotification from "../../../../base/hooks/components/useNotification";
import badgeStyles from "../../orderBadges.module.scss";

import placeholder from "../../../../assets/images/placeholder.png";
import styles from "./OrdersItemTable.module.scss";

export type OrdersItemTableProps = {
    getOrderLoading?: boolean;
    lineItems?: LineItem[] | null;
    loading?: boolean;
    showEditForm?: boolean;
    updating?: boolean;
    handleAddItemQuantity: (index: number) => void;
    handleSubItemQuantity: (index: number) => void;
    selection: number[];
    setSelection: (selection: number[]) => void;
    widthColumnPrice?: string;
    namesProductsPOS?: OrderLineItemProductName[];
};

type ItemRow = LineItem & {
    index: number;
    pos_name?: string;
    /** Present when GraphQL returns it; opens product page in a new tab. */
    product_url?: string | null;
};

const MinusIcon = () => <Icon icon="minus" />;
const PlusIcon = () => <Icon icon="plus" />;
const TrashIcon = () => <Icon icon="delete" />;
const CopyIcon = () => <Icon icon="copy" />;

const cellText = (value?: string | null) => (
    <Text as="span" variant="body-md">
        {value || NOT_AVAILABLE}
    </Text>
);

export const OrdersItemTable: FC<OrdersItemTableProps> = ({
    getOrderLoading,
    lineItems,
    loading,
    showEditForm = false,
    updating,
    handleAddItemQuantity,
    handleSubItemQuantity,
    selection,
    setSelection,
    namesProductsPOS,
}) => {
    const notify = useNotification();

    const rows: ItemRow[] = useMemo(
        () =>
            (lineItems ?? []).map((item, index) => ({
                ...item,
                index,
                pos_name: namesProductsPOS?.find(p => p.id === item.pos_id)
                    ?.name,
            })),
        [lineItems, namesProductsPOS],
    );

    const clipboardCopy = (item: ItemRow) => {
        const nameItem = item.name || NOT_AVAILABLE;
        const text = item.pos_name || nameItem;
        navigator.clipboard.writeText(text);
        notify(`${text} is copied`, "success");
    };

    const columns: TableColumn<ItemRow>[] = [
        {
            id: "thumbnail",
            header: "Image",
            width: 72,
            minWidth: 72,
            renderCell: row => {
                const thumb = (
                    <img
                        alt=""
                        className={styles.thumb}
                        height={51}
                        src={row.thumbnail || placeholder}
                        width={51}
                    />
                );

                return row.product_url ? (
                    <a
                        aria-label={`Open ${
                            row.name || "product"
                        } in a new tab`}
                        className={styles.thumbLink}
                        href={row.product_url}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {thumb}
                    </a>
                ) : (
                    thumb
                );
            },
        },
        {
            id: "name",
            header: "Name",
            minWidth: 160,
            renderCell: row => (
                <div className={styles.nameCell}>
                    {row.product_url ? (
                        <a
                            className={`${styles.nameText} ${styles.nameLink}`}
                            href={row.product_url}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            {row.name || NOT_AVAILABLE}
                        </a>
                    ) : (
                        <Text
                            as="span"
                            className={styles.nameText}
                            variant="body-md"
                        >
                            {row.name || NOT_AVAILABLE}
                        </Text>
                    )}
                    <IconButton
                        aria-label="Copy POS product name"
                        onClick={() => clipboardCopy(row)}
                        size="sm"
                        title="Copy POS product name"
                        type="button"
                        variant="tertiary"
                    >
                        <CopyIcon />
                    </IconButton>
                    {!row.pos_id ? (
                        <Badge
                            className={badgeStyles.statusBadge}
                            data-tone="error"
                        >
                            Empty pos id
                        </Badge>
                    ) : null}
                </div>
            ),
        },
        {
            id: "brand",
            header: "Brand",
            minWidth: 96,
            renderCell: row => cellText(row.brand),
        },
        {
            id: "category",
            header: "Category",
            minWidth: 96,
            renderCell: row => cellText(row.category),
        },
        {
            id: "weight",
            header: "Weight",
            minWidth: 96,
            renderCell: row =>
                cellText(
                    row.variants?.[0]?.type
                        ? startCase(row.variants[0].type)
                        : null,
                ),
        },
        {
            id: "quantity",
            header: "Quantity",
            align: "center",
            width: showEditForm ? 140 : 88,
            minWidth: showEditForm ? 140 : 88,
            renderCell: row =>
                showEditForm ? (
                    <div className={styles.quantity}>
                        <IconButton
                            aria-label={
                                row.quantity === 1
                                    ? `Remove ${row.name || "item"}`
                                    : `Decrease quantity of ${
                                          row.name || "item"
                                      }`
                            }
                            onClick={() => handleSubItemQuantity(row.index)}
                            size="sm"
                            type="button"
                            variant="tertiary"
                        >
                            {row.quantity === 1 ? <TrashIcon /> : <MinusIcon />}
                        </IconButton>
                        <Text
                            as="span"
                            className={styles.quantityCount}
                            variant="body-md"
                        >
                            {row.quantity ?? NOT_AVAILABLE}
                        </Text>
                        <IconButton
                            aria-label={`Increase quantity of ${
                                row.name || "item"
                            }`}
                            onClick={() => handleAddItemQuantity(row.index)}
                            size="sm"
                            type="button"
                            variant="tertiary"
                        >
                            <PlusIcon />
                        </IconButton>
                    </div>
                ) : (
                    cellText(row.quantity != null ? String(row.quantity) : null)
                ),
        },
        {
            id: "barcodes",
            header: "Barcodes",
            minWidth: 120,
            renderCell: row =>
                row.barcodes?.length ? (
                    <ul className={styles.barcodes}>
                        {row.barcodes.map((code, index) => (
                            <li key={`${row.index}-${index}`}>{code}</li>
                        ))}
                    </ul>
                ) : (
                    cellText(null)
                ),
        },
        {
            id: "price",
            header: "Price",
            width: 96,
            minWidth: 96,
            renderCell: row => (
                <Text as="span" variant="body-md">
                    {row.price != null ? currencyFormat(row.price) : "$0"}
                </Text>
            ),
        },
    ];

    const selectedRowIds = selection.map(String);

    const handleSelectedRowIdsChange = (rowIds: string[]) => {
        setSelection(
            rowIds.map(id => Number(id)).filter(n => !Number.isNaN(n)),
        );
    };

    const isLoading = Boolean(loading || updating || getOrderLoading);

    return (
        <div className={styles.root}>
            <div className={styles.tableWrap}>
                <Table
                    columns={columns}
                    emptyMessage={
                        isLoading ? "Loading items…" : "No items ordered"
                    }
                    getRowId={row => String(row.index)}
                    onSelectedRowIdsChange={handleSelectedRowIdsChange}
                    rows={rows}
                    selectable={showEditForm}
                    selectedRowIds={selectedRowIds}
                    showEmptyGraphic={false}
                />
            </div>
        </div>
    );
};

export default OrdersItemTable;
